import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { ForEachActivity } from 'app/model/pipeline/foreach.activity';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Owned } from 'app/model/base/named';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';
import { generateUUID } from 'app/shared/utils/uuid.util';
import {
    IForEachActivityInterimContext,
    IPipelineCopilotCurrentPipelineContext,
    PipelineActivityInterimContext
} from 'app/interfaces/copilot.pipeline.action.types';
import { Activity, arrayIsNullOrEmpty } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(ForEachActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            isSequential: '*',
            batchCount: '*',
            items: '*',
            activities: '*'
        }
    }
})
export class ForEachActivityAdapter extends ActivityAsyncAdapter<
    ForEachActivity,
    Model.ForEachActivity,
    IForEachActivityInterimContext
> {
    public override async toFabricCopilotContext(
        forEachActivity: ForEachActivity
    ): Promise<IForEachActivityInterimContext> {
        if (forEachActivity) {
            const foreachActivityContext: IForEachActivityInterimContext = {
                ...(await super.toFabricCopilotContext(forEachActivity)),
                ItemExpression: ForEachActivity.ITEMS.templateValue(forEachActivity),
                BatchCount: forEachActivity.batchCount,
                IsSequential: forEachActivity.isSequential
            };
            const activities: PipelineActivityInterimContext[] = [];
            for (const activity of forEachActivity.pipeline?.activities ?? []) {
                const adapter = this.context.asyncAdapterFor<Activity, Model.Activity, PipelineActivityInterimContext>(
                    Metamodel.modelOf(activity)
                );
                const activityContext = await adapter.toFabricCopilotContext(activity);
                if (activityContext) {
                    activities.push(activityContext);
                }
            }
            if (activities.length > 0) {
                foreachActivityContext.ChildActivities = activities;
            }
            return foreachActivityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IForEachActivityInterimContext,
        model?: ForEachActivity
    ): Promise<ForEachActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.ItemExpression != null) {
                ForEachActivity.ITEMS.setTemplateValue(model, context.ItemExpression);
            }
            if (!model.pipeline) {
                model.pipeline = Owned.create<Pipeline>(Pipeline.MODEL, model);
                model.pipeline.name = generateUUID();
            }
            if (!arrayIsNullOrEmpty(context.ChildActivities)) {
                const adapter = this.context.asyncAdapterFor<
                    Pipeline,
                    Model.PipelineResource,
                    IPipelineCopilotCurrentPipelineContext
                >(Pipeline.MODEL);
                await adapter.fromFabricCopilotContext(
                    {
                        Activities: context.ChildActivities
                    },
                    model.pipeline
                );
            } else {
                model.pipeline.activities.clear();
            }
            if (context.IsSequential != null) {
                model.isSequential = context.IsSequential;
            }
            if (context.BatchCount != null) {
                model.batchCount = context.BatchCount;
            }
        }
        return model;
    }

    public override async postToModel(activity: ForEachActivity): Promise<void> {
        await super.postToModel(activity);

        await Promise.all(
            Array.from(activity.pipeline.activities.values()).map(async (innerActivity) => {
                const activityModel = Metamodel.modelOf(innerActivity);
                const adapter = this.context.isAsyncAdapter(activityModel)
                    ? this.context.asyncAdapterFor(activityModel)
                    : this.context.adapterFor(activityModel);
                try {
                    await adapter?.postToModel(innerActivity);
                } catch (e) {
                    this._logError(
                        `Activity "${innerActivity.name}" \`postToModel\` failed in foreach activity of pipeline "${activity.pipeline.name}"`,
                        e
                    );
                }
            })
        );
    }

    protected async _toResource(activity: ForEachActivity): Promise<Model.ForEachActivity> {
        const modelForEachActivity: Model.ForEachActivity = super._getCommonActivityProperties(
            activity,
            'ForEach'
        ) as Model.ForEachActivity;
        modelForEachActivity.typeProperties = modelForEachActivity.typeProperties || { items: undefined };

        modelForEachActivity.typeProperties.isSequential = activity.isSequential;

        if (!ValueAdapter.isTemplated(activity, ForEachActivity.ITEMS)) {
            // the items propery only accepts expressions, turn the regular value into an expresion
            ForEachActivity.ITEMS.setTemplateValue(activity, ForEachActivity.ITEMS.value(activity));
        }

        modelForEachActivity.typeProperties.items = ValueAdapter.toResource(activity, ForEachActivity.ITEMS);

        if (activity.isSequential !== true) {
            modelForEachActivity.typeProperties.batchCount = activity.batchCount;
        }

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);

        if (adapter) {
            const pipeline = await adapter.toResource(activity.pipeline);
            if (pipeline.properties) {
                modelForEachActivity.typeProperties.activities = pipeline.properties.activities;
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelForEachActivity, parsed);
        }

        return modelForEachActivity;
    }

    protected async _toModel(modelActivity: Model.ForEachActivity): Promise<ForEachActivity> {
        const activity: ForEachActivity = ForEachActivity.MODEL.create<ForEachActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { items: undefined };

        activity.isSequential = modelActivity.typeProperties.isSequential;
        activity.batchCount = modelActivity.typeProperties.batchCount;

        ValueAdapter.toModel(activity, ForEachActivity.ITEMS, modelActivity.typeProperties.items);

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);

        if (adapter) {
            const tempPipeline: Model.PipelineResource = {
                name: `${modelActivity.name}$$pipeline`,
                type: 'pipeline',
                properties: { activities: modelActivity.typeProperties.activities }
            };
            activity.pipeline = Owned.create<Pipeline>(Pipeline.MODEL, activity);
            await adapter.toModel(tempPipeline, activity.pipeline);
            // Nested pipeline name is unnecessary for ForEach model, it's just used as a workaround of Pipeline naming validator.
            activity.pipeline.name = generateUUID();
        }

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, ForEachActivity.MODEL));
        return activity;
    }
}
